/*!
 * Copyright 2026 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useMemo, useState } from "react";

import { useGetSessionsQuery } from "./api/sessionsV2.api";
import { DEFAULT_POLLING_INTERVAL_MS } from "./session.constants";
import { SessionStatusState } from "./sessionsV2.types";

interface UseWaitForSessionStatusArgs {
  desiredStatus: SessionStatusState | SessionStatusState[];
  pollingInterval?: number;
  sessionName: string;
  skip?: boolean;
}

export function useWaitForSessionStatusV2({
  desiredStatus,
  pollingInterval = DEFAULT_POLLING_INTERVAL_MS,
  sessionName,
  skip,
}: UseWaitForSessionStatusArgs) {
  const [isWaiting, setIsWaiting] = useState(false);

  const result = useGetSessionsQuery(
    {},
    {
      pollingInterval,
      skip: skip || !isWaiting,
    },
  );
  const session = useMemo(() => {
    if (result.data == null) {
      return undefined;
    }
    return Object.values(result.data).find(({ name }) => name === sessionName);
  }, [result.data, sessionName]);

  useEffect(() => {
    if (skip) {
      // TODO: fix react-hooks/set-state-in-effect
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsWaiting(false);
    }
  }, [skip]);

  useEffect(() => {
    if (skip) {
      return;
    }
    const desiredStatuses =
      typeof desiredStatus === "string" ? [desiredStatus] : desiredStatus;
    const isWaiting =
      (session != null && !desiredStatuses.includes(session.status.state)) ||
      (session == null && !desiredStatuses.includes("stopping"));
    // TODO: fix react-hooks/set-state-in-effect
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsWaiting(isWaiting);
  }, [desiredStatus, session, skip]);

  return { isWaiting, session };
}
